import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminImports() {
  const { tenantId } = useAuth();
  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", source_type: "google_sheet", framework: "dinacharya", sync_mode: "manual_upload" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/import-sources`).then(({ data }) => setSources(data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/tenants/${tenantId}/import-jobs`).then(({ data }) => setJobs(data)).catch(() => {});
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-imports-header"
        eyebrow="Administration"
        title="Imports & integration jobs"
        description="Sheets are an ingestion source behind a mapping layer — never the system of record. Jobs are validated before any canonical write."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-import-source-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">New source</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-display text-2xl">Import source</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps">Name</Label>
                  <Input data-testid="import-name-input" className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Source type</Label>
                  <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                    <SelectTrigger data-testid="import-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["google_sheet", "excel_upload", "csv_upload", "webhook"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Framework</Label>
                  <Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}>
                    <SelectTrigger data-testid="import-framework-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["dinacharya", "panchatanthra", "viveka_kartha"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Sync mode</Label>
                  <Select value={form.sync_mode} onValueChange={(v) => setForm({ ...form, sync_mode: v })}>
                    <SelectTrigger data-testid="import-sync-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["manual_upload", "scheduled_pull", "webhook_sync"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="create-import-source-submit"
                  className="w-full rounded-none"
                  disabled={!form.name}
                  onClick={async () => {
                    try {
                      await api.post(`/tenants/${tenantId}/import-sources`, { ...form, mapping: {} });
                      toast.success("Import source registered");
                      setOpen(false);
                      setForm({ name: "", source_type: "google_sheet", framework: "dinacharya", sync_mode: "manual_upload" });
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  Register source
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <p className="label-caps mb-4">Sources</p>
          <div className="panel divide-y divide-border" data-testid="import-sources-list">
            {sources.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-5 py-4">
                <div>
                  <p className="text-sm">{s.name}</p>
                  <p className="label-caps mt-1">{s.source_type} · {s.framework} · {s.sync_mode}</p>
                </div>
                <Button
                  data-testid={`run-import-${s.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                  onClick={async () => {
                    try {
                      await api.post(`/tenants/${tenantId}/import-sources/${s.id}/run`);
                      toast.success("Import job queued for validation");
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  Run job
                </Button>
              </div>
            ))}
            {sources.length === 0 && <EmptyState testid="import-sources-empty" title="No sources" message="Register an ingestion source with its mapping." />}
          </div>
        </div>
        <div>
          <p className="label-caps mb-4">Job monitor</p>
          <div className="panel divide-y divide-border" data-testid="import-jobs-list">
            {jobs.map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-3 px-5 py-4">
                <div>
                  <p className="text-sm">{j.source_name}</p>
                  <p className="label-caps mt-1">{new Date(j.created_at).toLocaleString()} · {j.rows_read} rows read</p>
                  <p className="mt-1 text-xs text-muted-foreground">{(j.messages || []).join(" ")}</p>
                </div>
                <StateBadge state={j.status} />
              </div>
            ))}
            {jobs.length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No jobs run yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
